import { useEffect, useRef, useState } from 'react'
import type { Asset } from '@/types'
import { computeOvertimeState } from '@/lib/generationTiming'
import type { ProgressState } from '@/lib/generationTiming'

export type V9TimedPhase =
  'preparing_scene' | 'rendering_transition' | 'generating_shot' | 'extracting_frame' | 'upscaling'

interface V9PhaseProgressBand {
  checkpoint: number
  softCeiling: number
  estimatedDurationMs: number
}

const DISPLAY_UPDATE_INTERVAL_MS = 1000

export const V9_PHASE_PROGRESS_BANDS: Record<V9TimedPhase, V9PhaseProgressBand> = {
  preparing_scene: {
    checkpoint: 2,
    softCeiling: 44,
    estimatedDurationMs: 368_000,
  },
  rendering_transition: {
    checkpoint: 5,
    softCeiling: 44,
    estimatedDurationMs: 368_000,
  },
  generating_shot: {
    checkpoint: 45,
    softCeiling: 77,
    estimatedDurationMs: 276_000,
  },
  extracting_frame: {
    checkpoint: 78,
    softCeiling: 79,
    estimatedDurationMs: 2_000,
  },
  upscaling: {
    checkpoint: 80,
    softCeiling: 89,
    estimatedDurationMs: 108_000,
  },
}

function isTimedPhase(phase: string | null | undefined): phase is V9TimedPhase {
  return (
    phase === 'preparing_scene' ||
    phase === 'rendering_transition' ||
    phase === 'generating_shot' ||
    phase === 'extracting_frame' ||
    phase === 'upscaling'
  )
}

interface V9ProgressSnapshot {
  operationId?: string
  failed?: boolean
  progress?: number
  phase?: string
  phaseStartedAt?: string
}

export function calculateV9DisplayProgress(
  snapshot: V9ProgressSnapshot | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!snapshot?.operationId || snapshot.failed) return null

  const rawProgress = typeof snapshot.progress === 'number' ? Math.round(snapshot.progress) : null
  if (!isTimedPhase(snapshot.phase)) return rawProgress

  const band = V9_PHASE_PROGRESS_BANDS[snapshot.phase]
  const checkpointFloor = Math.max(band.checkpoint, rawProgress ?? band.checkpoint)

  if (!snapshot.phaseStartedAt) return checkpointFloor

  const phaseStartedAtMs = Date.parse(snapshot.phaseStartedAt)
  if (!Number.isFinite(phaseStartedAtMs)) return checkpointFloor

  const elapsedMs = Math.max(0, now - phaseStartedAtMs)
  const ratio = Math.min(elapsedMs / band.estimatedDurationMs, 1)
  const interpolated = band.checkpoint + ratio * (band.softCeiling - band.checkpoint)

  return Math.round(Math.min(band.softCeiling, Math.max(checkpointFloor, interpolated)))
}

type V9AssetProgressFields = Pick<
  Asset,
  'v9ShotOperationId' | 'v9ShotFailed' | 'v9ShotProgress' | 'v9ShotPhase' | 'v9ShotPhaseStartedAt'
>

function toSnapshot(asset: V9AssetProgressFields | null | undefined): V9ProgressSnapshot | null {
  if (!asset) return null
  return {
    operationId: asset.v9ShotOperationId,
    failed: asset.v9ShotFailed ?? false,
    progress: asset.v9ShotProgress,
    phase: asset.v9ShotPhase,
    phaseStartedAt: asset.v9ShotPhaseStartedAt,
  }
}

/**
 * Returns the current soft ceiling for a given phase, or null if the phase is not timed.
 */
function getCurrentPhaseCeiling(phase: string | null | undefined): number | null {
  if (!isTimedPhase(phase)) return null
  return V9_PHASE_PROGRESS_BANDS[phase].softCeiling
}

export function useV9DisplayProgress(asset: V9AssetProgressFields | null | undefined): ProgressState {
  const operationId = asset?.v9ShotOperationId
  const failed = asset?.v9ShotFailed
  const progress = asset?.v9ShotProgress
  const phase = asset?.v9ShotPhase
  const phaseStartedAt = asset?.v9ShotPhaseStartedAt

  const [state, setState] = useState<ProgressState>(() => {
    const pct = calculateV9DisplayProgress({ operationId, failed, progress, phase, phaseStartedAt })
    return { percentage: pct, isOvertime: false, overtimeMessage: null }
  })
  const lastValueRef = useRef<number | null>(state.percentage)
  const ceilingReachedAtRef = useRef<number | null>(null)
  const lastPhaseRef = useRef<string | null | undefined>(phase)
  const lastOvertimeMessageRef = useRef<string | null>(null)

  useEffect(() => {
    const snapshot = { operationId, failed, progress, phase, phaseStartedAt }

    // Reset ceiling tracking when phase changes
    if (phase !== lastPhaseRef.current) {
      ceilingReachedAtRef.current = null
      lastOvertimeMessageRef.current = null
      lastPhaseRef.current = phase
    }

    // No active operation — compute once and skip the interval entirely
    if (!operationId || failed) {
      const next = calculateV9DisplayProgress(snapshot)
      if (next !== lastValueRef.current) {
        lastValueRef.current = next
        setState({ percentage: next, isOvertime: false, overtimeMessage: null })
      }
      ceilingReachedAtRef.current = null
      lastOvertimeMessageRef.current = null
      return
    }

    const ceiling = getCurrentPhaseCeiling(phase)

    function tick() {
      const next = calculateV9DisplayProgress(snapshot)

      // Track when display progress hits the phase's soft ceiling
      if (ceiling !== null && next !== null && next >= ceiling && ceilingReachedAtRef.current === null) {
        ceilingReachedAtRef.current = Date.now()
      }

      // Compute overtime state
      let overtime = { isOvertime: false, overtimeMessage: null as string | null }
      if (ceilingReachedAtRef.current !== null) {
        overtime = computeOvertimeState(Date.now() - ceilingReachedAtRef.current)
      }

      // Only re-render when value or overtime tier changes
      if (next !== lastValueRef.current || overtime.overtimeMessage !== lastOvertimeMessageRef.current) {
        lastValueRef.current = next
        lastOvertimeMessageRef.current = overtime.overtimeMessage
        setState({ percentage: next, ...overtime })
      }
    }

    tick()

    const intervalId = window.setInterval(tick, DISPLAY_UPDATE_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [operationId, failed, progress, phase, phaseStartedAt])

  return state
}
